import React, { useId } from 'react';
import { cx } from '@/ui/utils/cx';

export type ToggleProps = {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Used when there is no visible label text. */
  'aria-label'?: string;
  /** Inline layout for toolbars, filters, and table cells. */
  compact?: boolean;
  name?: string;
  required?: boolean;
};

export default function Toggle({
  id: idProp,
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  compact = false,
  name,
  required,
}: ToggleProps) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const hasVisibleLabel = label != null && label !== '';

  return (
    <div
      className={cx(
        'settings-field',
        'settings-field--toggle',
        compact && 'settings-field--toggle-compact',
        className,
      )}
    >
      <label className="settings-toggle" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          role="switch"
          className="settings-toggle__input"
          checked={checked}
          disabled={disabled}
          required={required}
          name={name}
          onChange={(event) => onChange(event.target.checked)}
          aria-checked={checked}
          aria-label={!hasVisibleLabel ? ariaLabel : undefined}
          aria-describedby={hintId}
        />
        <span className="settings-toggle__track" aria-hidden="true">
          <span className="settings-toggle__thumb" />
        </span>
        {hasVisibleLabel || hint ? (
          <span className="settings-toggle__label">
            {hasVisibleLabel ? <span className="settings-field__label-text">{label}</span> : null}
            {hint ? (
              <span className="settings-field__hint" id={hintId}>
                {hint}
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
    </div>
  );
}

/** Alias for product docs and call sites that prefer the Scoutly name. */
export { Toggle as ScoutlyToggle };
